import { DestroyRef, Directive, DOCUMENT, ElementRef, inject, input, InputSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormControlDirective } from '@angular/forms';

import { deleteField, FieldValue, UpdateData } from 'firebase/firestore';
import { fromEvent, map, Observable, switchMap } from 'rxjs';

import { DocInstance } from '../../data/daos/doc-instance';
import { toDotNotation } from '../utils/dot-notation.util';

export interface StreamingInputConfig<T extends object, TValue> {
	docToValue: (doc: T, key?: string) => TValue;
	valueToDoc: (value: TValue | FieldValue, key?: string) => UpdateData<T>;
}

@Directive()
export abstract class StreamingInputBaseDirective<T extends object, TValue> implements OnInit {
	abstract readonly config: InputSignal<StreamingInputConfig<T, TValue>>;
	doc = input.required<DocInstance<T>>();
	key = input<string>();

	protected readonly document = inject(DOCUMENT);
	protected readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
	protected readonly formControlDir = inject(FormControlDirective, { self: true });
	private readonly destroyRef = inject(DestroyRef);

	private readonly data$ = toObservable(this.doc).pipe(switchMap((doc) => doc.stream));

	ngOnInit(): void {
		const control = this.formControlDir.control as FormControl<TValue>;
		const modelValue$ = this.data$.pipe(
			map((data) => (data == null ? null : this.config().docToValue(data, this.key()))),
		);

		let modelValue: TValue | null = null;
		modelValue$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			modelValue = value;
			if (value == null || this.isEditing()) return;
			if (control.value !== value) {
				control.setValue(value, { emitEvent: false });
				control.markAsDirty();
			}
		});

		this.changeObservable()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				const value = control.value;
				const model = modelValue;
				if (control.invalid) return;
				const shouldDelete = value == null && model != null;
				const shouldUpdate = value != null && value !== model;
				if (!shouldDelete && !shouldUpdate) return;
				const next = value == null ? deleteField() : value;
				void this.doc().update(toDotNotation(this.config().valueToDoc(next, this.key())));
			});
	}

	abstract changeObservable(): Observable<unknown>;

	private isEditing(): boolean {
		return this.host.contains(this.document.activeElement);
	}
}

@Directive({
	selector: '[formControl][dixStreamingInput]',
})
export class StreamingInputDirective<T extends object, TValue> extends StreamingInputBaseDirective<T, TValue> {
	// eslint-disable-next-line @angular-eslint/no-input-rename
	override readonly config = input.required<StreamingInputConfig<T, TValue>>({ alias: 'dixStreamingInput' });

	override changeObservable(): Observable<unknown> {
		return this.formControlDir.control.valueChanges;
	}
}

@Directive({
	selector: '[formControl][dixStreamingTextInput]',
})
export class StreamingTextInputDirective<T extends object, TValue> extends StreamingInputBaseDirective<T, TValue> {
	// eslint-disable-next-line @angular-eslint/no-input-rename
	override readonly config = input.required<StreamingInputConfig<T, TValue>>({ alias: 'dixStreamingTextInput' });

	override changeObservable(): Observable<unknown> {
		return fromEvent(this.host, 'focusout');
	}
}
